import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

interface Peek { email: string; clientName: string; }

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refreshClient } = useAuth();
  const [peek, setPeek] = useState<Peek | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing token.'); return; }
    fetch(`/api/invites/peek/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).error ?? 'peek_failed');
        return r.json();
      })
      .then(setPeek)
      .catch((e) => setError(peekErrorMessage(e.message)));
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) { setError('Password must be at least 12 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch('/api/invites/accept', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, displayName: displayName || undefined }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: 'accept_failed' }));
        throw new Error(body.error ?? 'accept_failed');
      }
      await refreshClient();
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Accept failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !peek) return <div style={{ padding: 32 }}>Invite error: {error}</div>;
  if (!peek) return <div style={{ padding: 32 }}>Loading invite…</div>;

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--mi-bg)' }}>
      <form onSubmit={onSubmit} className="card" style={{ width: 420 }}>
        <h1 style={{ color: 'var(--mi-navy)', marginTop: 0 }}>You're invited</h1>
        <p>Welcome to <b>{peek.clientName}</b>.</p>
        <p style={{ color: 'var(--mi-muted)', marginTop: 0 }}>Set a password to finish creating your account.</p>

        <label style={label}>
          Email
          <input type="email" value={peek.email} disabled style={input} />
        </label>
        <label style={label}>
          Your name (optional)
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            style={input}
          />
        </label>
        <label style={label}>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
            style={input}
          />
          <small style={hint}>At least 12 characters.</small>
        </label>
        <label style={label}>
          Confirm password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
            style={input}
          />
        </label>
        {error && <div style={err}>{error}</div>}
        <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Setting up…' : 'Accept invite'}
        </button>
      </form>
    </div>
  );
}

function peekErrorMessage(code: string): string {
  switch (code) {
    case 'not_found': return 'Invite not found.';
    case 'already_accepted': return 'This invite has already been accepted.';
    case 'expired': return 'This invite has expired.';
    default: return code;
  }
}

const label: React.CSSProperties = { display: 'block', marginBottom: 12 };
const input: React.CSSProperties = { width: '100%', padding: '0.5rem', marginTop: 4, borderRadius: 6, border: '1px solid var(--mi-border)' };
const hint: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 12 };
const err: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
